import { Injectable } from "@angular/core";
import { Auth } from "../infrastructure/auth";
import { Logger } from "../infrastructure/logger";
import { TagRepository } from "../domain/tag-repository";
import { CachedTagRepositoryImpl } from "../infrastructure/repositories/tags/tag";
import { UserRepository } from "../domain/user-repository";
import { UsersRepositoryImpl } from "../infrastructure/repositories/user/user";
import { UserService } from "../domain/user-service";
import { ChannelRepository } from "../domain/channel-repository";
import { ChannelRepositoryImpl } from "../infrastructure/repositories/channel/channel";
import { Socket } from "../infrastructure/socket.service";
import { ShowRepository } from "../domain/show-repository";
import { ChannelTemplateRepository } from "../domain/channel-template-repository";
import { ChannelTemplateRepositoryCached } from "../infrastructure/repositories/channel/template/channel-template-repository";
import { CategoryRepository } from "../domain/category-repository";
import { CategoryRepositoryImpl } from "../infrastructure/repositories/category/category";
import { CachedTenantSettingsRepositoryImpl } from "../infrastructure/repositories/tenant/tenantSettings";
import { TenantSettingsRepository } from "../domain/tenant-settings-repository";
import { RecordingService } from "./recording/recording.service";
import { MediaService } from "./media/media.service";
import { HttpService } from "../infrastructure/http/http";
import { ICloudStorageProviderFactory } from "../domain/Upload/i-cloud-storage-provider-factory";
import { HttpClient } from "@angular/common/http";
import { UserBrandingService } from "../domain/user-branding-service";
import { SuperuserService } from "./superuser.service";
import { IBlockRepository } from "../domain/blocks/i-block.repository";
import { CachedBlocksRepository } from "../infrastructure/blocks/cached-blocks-repository.service";
import { IBroadcastsRepository } from "../domain/broadcasts/i-broadcasts-repository";
import { CachedBroadcastsRepository } from "../infrastructure/broadcasts/cached-broadcasts-repository";
import { IChannelBroadcastModeChecker } from "../domain/broadcasts/i-channel-broadcast-mode-checker";
import { DestinationsRepository } from "../infrastructure/repositories/destinations/destinations.repository";

import { environment } from "app/environments/environment";
@Injectable()
export class InitService {
  isLocalhost = environment.ENDPOINT.startsWith("http://localhost");
  constructor(
    private userRepository: UserRepository,
    private auth: Auth,
    private logger: Logger,
    private tagRepository: TagRepository,
    private userService: UserService,
    private cloudStorageProviderFactory: ICloudStorageProviderFactory,
    private channelRepository: ChannelRepository,
    private socketService: Socket,
    private showRepository: ShowRepository,
    private channelTemplatesRepository: ChannelTemplateRepository,
    private categoryRepository: CategoryRepository,
    private tenantSettingsRepository: TenantSettingsRepository,
    private mediaService: MediaService,
    private recordingService: RecordingService,
    private httpService: HttpService,
    private httpClient: HttpClient,
    private _userBrandingService: UserBrandingService,
    private superUserService: SuperuserService,
    private _blockRepository: IBlockRepository,
    private _broadcastPresetsRepository: IBroadcastsRepository,
    private _channelBroadcastModeChecker: IChannelBroadcastModeChecker,
    private _destinationsRepository: DestinationsRepository,
  ) {}

  getUserBrandingService(): UserBrandingService {
    return this._userBrandingService;
  }
  async init() {
    if (!this.auth.loggedIn()) {
      return (window.location.href = "/login");
    }

    try {
      await this.socketService.init();
      await this.showRepository.init();
      await (this.userRepository as UsersRepositoryImpl).init();
      await (this.tagRepository as CachedTagRepositoryImpl).init();
      await this.userService.init();
      await (this.channelRepository as ChannelRepositoryImpl).init();
      await (
        this.channelTemplatesRepository as ChannelTemplateRepositoryCached
      ).init();
      await (this.categoryRepository as CategoryRepositoryImpl).init();
      await this._destinationsRepository.init();
      const tenantSettings = await (
        this.tenantSettingsRepository as CachedTenantSettingsRepositoryImpl
      ).init();

      await this.cloudStorageProviderFactory
        .createInstance(
          this.httpService,
          this.userService,
          this.logger,
          this.auth,
          this.httpClient,
        )
        .init();
      await this.mediaService.init();
      if (this.isLocalhost) {
        (this._blockRepository as CachedBlocksRepository).init();
      } else {
        await (this._blockRepository as CachedBlocksRepository).init();
      }
      if (this.userService.getCurrentUser().isBroadcastsManager) {
        await (
          this._broadcastPresetsRepository as CachedBroadcastsRepository
        ).init();
      }
      await this.superUserService.handleChannelParameter();
      await this._channelBroadcastModeChecker.init();
    } catch (err) {
      this.logger.error(err);

      // Do not allow Angular to bootstrap user-dependent services after the
      // authoritative user lookup has failed. This is especially important
      // when the frontend is moved behind a different backend boundary: an
      // otherwise unexpired token can be rejected by that backend, leaving no
      // current user for services such as UploadService.
      if (!this.userService.getCurrentUser()) {
        this.auth.remove();
        this.redirectToLogin();
        throw err;
      }
    }
  }

  private redirectToLogin(): void {
    if (window.location.pathname !== "/login") {
      window.location.replace("/login");
    }
  }
}
